import { useEffect, useState } from "react";
import { ThunkDispatch } from "redux-thunk";
import { connect, ConnectedProps, useDispatch, useStore } from "react-redux";

import {
	Button,
	Space,
	Spin,
	Table,
	TablePaginationConfig,
	Typography,
	Row,
	Tag,
} from "antd";
import {
	FilterValue,
	SorterResult,
	TableCurrentDataSource,
} from "antd/lib/table/interface";
import { SyncOutlined } from "@ant-design/icons";
import UniqueSearchBar from "../../../Widget/UniqueSearchBar";
import { labels as labelsTranslator } from "../../../tools/mappings";
import { compareLocated } from "../../../../lib/location";
import { Map, Maybe } from "../../../../interfaces/Utils";
import { capitalize } from "../../../../lib/strings";
import { CloseAction, OpenAction } from "../../../../store/sidebar";
import {
	CurrentEditStore,
	fetchedAction,
	loadingAction,
	selectedNodesAction,
	specAction,
	ThunkFetchNode,
} from "../store";
import { IndicatorSpec, Node, NodeFetch } from "../../interface";
import trlfetch, { fetchIdxData } from "../common/linefetch";
import defaultMasks from "../common/masks";
import reIndex from "../common/reindex";
import FilterDropdown from "../common/FilterDropdown";
import RelationsDisplayer from "../RelationsDisplayer";
import MetaSectorSelector from "./MetaSectorSelector";

import "../../../../css/tools/elements.css";
import { renderSponsor } from "./handlers";
import "../../../../css/indicators/label.css";

const { Title, Paragraph } = Typography;

interface ColProps {
	title: string;
	dataIndex?: string | string[];
	render?: any;
	width?: number;
	fixed?: "left" | "right";
	align?: "left" | "center" | "right";
	sorter?: boolean;
}

type KeyedNode = Node & { key: number };

// #region store connection
interface StateProps {
	loading?: boolean;
	spec?: IndicatorSpec;
	selectedNodes: Node[];
	fetched: NodeFetch;
}

function mapStateToProps({
	edit: { loading, spec, selectedNodes, fetched },
}: CurrentEditStore): StateProps {
	return {
		loading: loading,
		spec: spec,
		selectedNodes: selectedNodes,
		fetched: fetched,
	};
}

interface DispatchProps {
	setLoading(v: boolean): void;
	setSpec(spec: IndicatorSpec): void;
	setSelectedNodes(nodes: Node[]): void;
	setFetched(fetched: NodeFetch): void;
	changeSize(page: number, labels: string[]): void;
}

function mapDispatchToProps(
	dispatch: ThunkDispatch<CurrentEditStore, void, any>
): DispatchProps {
	return {
		setLoading: function setLoading(v: boolean): void {
			dispatch(loadingAction(v));
		},
		setSpec: function setSpec(spec: IndicatorSpec): void {
			dispatch(specAction(spec));
		},
		setSelectedNodes: function setSelectedNodes(nodes: Node[]): void {
			dispatch(selectedNodesAction(nodes));
		},
		setFetched: function setFetched(fetched: NodeFetch): void {
			dispatch(fetchedAction(fetched));
		},
		changeSize: function changeSize(page: number, labels: string[]) {
			dispatch(ThunkFetchNode(labels, 1, page));
		},
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps);
// #endregion

interface NodeListEditProps extends ConnectedProps<typeof connector> {
	labels: string[];
	searchParams: Map<string[]>;
}

function NodeListEdit(props: NodeListEditProps) {
	const {
		labels,
		searchParams,
		fetched,
		setFetched,
		loading,
		spec,
		selectedNodes,
		setLoading,
		setSpec,
		changeSize,
	} = props;

	const [edited, setEdited] = useState<number[]>([]);

	const {
		login: { userId },
		sidebar: { collapsed, data },
	}: CurrentEditStore = useStore().getState();

	let masks: Maybe<Map<string>>;
	if (labels) {
		masks = spec?.metas
			.find((m) => m.labels.split(",").sort().join(".") === labels.sort().join("."))
			?.props.reduce(
				(acc, { name, mask }) => ({
					...acc,
					[name]: mask,
				}),
				{} as Map<string>
			);
		if (masks) {
			masks = {
				...masks,
				flag: "Alerte(s)",
			};
		}
	}

	const noded: KeyedNode[] = fetched.values.map((f, i) => ({
		labels: labels,
		attributes: f,
		key: i,
	}));

	// const onChange = onChangeUncurr(searchParams, labels, pagNodes, setPagNodes, userId);
	const fetchOnChange = trlfetch({
		searchParams: searchParams,
		labels: labels,
		fetched: fetched,
		setFetched: setFetched,
		userId: userId,
	});

	const reIndexCurried = reIndex(fetchOnChange, labels, setLoading, userId);

	useEffect(() => {
		// onChange();
		fetchOnChange();
		if (labels && labels.length > 0) {
			fetchIdxData(
				userId,
				labels.find((s) => s.startsWith("IDX_")),
				async (r) => {
					if (!r.ok) {
						// TODO: handle error
						console.error(
							`error fetching counts: code: ${r.status} - message: ${r.statusText}`
						);
						return;
					}
					const specs = await r.json();
					setSpec(specs);
				}
			);
		}
	}, [labels]);

	// #region Sidebar
	const reduxDispatch = useDispatch();
	function closeSidebar() {
		reduxDispatch(CloseAction);
	}
	function openEditSidebar(editData: Node): () => void {
		return () => reduxDispatch(OpenAction("node-edit", editData));
	}
	// #endregion

	// #region Edit
	function renderEdit(uid: string, node: Node) {
		const thisAndOpen =
			!!data &&
			data.attributes &&
			(data as Node).attributes.uid === uid &&
			!collapsed;
		const onClick = thisAndOpen ? closeSidebar : openEditSidebar(node);
		const text = thisAndOpen ? "Fermer" : "Éditer";
		return (
			<Button size="small" onClick={onClick} type="link">
				{text}
			</Button>
		);
	}
	// #endregion

	// #region ExpandedRow
	function renderExpandedRow(record: { key: number } & Node) {
		const { ...node } = record;
		return <RelationsDisplayer node={node} userId={userId} />;
	}
	// #endregion

	// #region renderMetaSector
	function renderMetaSector(_: string, node: Node, index: number) {
		return (
			<MetaSectorSelector
				node={node}
				index={index}
				fetched={fetched}
				setFetched={setFetched}
				edited={edited}
				setEdited={setEdited}
				setLoading={setLoading}
				labels={labels}
				userId={userId}
			/>
		);
	}
	// #endregion

	// #region onTableChange
	function onTableChange(
		_pagination: TablePaginationConfig,
		_filters: Record<string, FilterValue | null>,
		sorter: SorterResult<KeyedNode> | SorterResult<KeyedNode>[],
		extra: TableCurrentDataSource<KeyedNode>
	): void {
		if (extra.action !== "sort" || Array.isArray(sorter)) {
			return;
		}
		const { field, order, column } = sorter;
		let attr: string;
		let orderAsc: boolean;
		let newFetched: NodeFetch;
		if (column === undefined || field === undefined) {
			if (Array.isArray(field) && field.length >= 2 && field[1] === "_sort_count") {
				newFetched = {
					...fetched,
					sortCount: undefined,
				};
			} else {
				const { sort, ...restFetched } = fetched;
				newFetched = restFetched;
			}
		} else {
			if (Array.isArray(field)) {
				attr = `${field[1]}`;
			} else {
				attr = `${field}`;
			}
			orderAsc = order !== "descend";
			if (attr === "_sort_count") {
				newFetched = {
					...fetched,
					sortCount: orderAsc,
				};
			} else {
				newFetched = {
					...fetched,
					sort: {
						key: attr,
						asc: orderAsc,
					},
				};
			}
		}
		trlfetch({
			searchParams: searchParams,
			labels: labels,
			fetched: newFetched,
			setFetched: setFetched,
			userId: userId,
		})();
	}
	// #endregion

	// #region updateSearch
	function updateSrcKV(key: string): (values: string[]) => void {
		return (values: string[]) => {
			const { src } = fetched;
			const newFetched = {
				...fetched,
				src: {
					...src,
					[key]: values,
				},
			};

			trlfetch({
				searchParams: searchParams,
				labels: labels,
				fetched: newFetched,
				setFetched: setFetched,
				userId: userId,
			})();
		};
	}
	// #endregion

	// #region columnsGen
	let cols: ColProps[] = [];
	const label = labels.filter((s) => !s.startsWith("IDX_"))[0];
	const labelTitle = labelsTranslator[label];
	if (noded && noded.length > 0) {
		const ignoreList = ["uid", "middle", "upper", "parent", "_from", "_to"];
		cols = Object.keys(noded[0].attributes)
			.filter((k) => ignoreList.indexOf(k) === -1)
			.sort(compareLocated)
			.map((k) => {
				const title = (masks ?? {})[k] ?? defaultMasks[k] ?? capitalize(k);
				return {
					title: title,
					dataIndex: ["attributes", k],
					filterDropdown: () => {
						return (
							<FilterDropdown
								searchkey={k}
								label={label}
								values={fetched.src[k]}
								updateValues={updateSrcKV(k)}
							/>
						);
					},
					sorter: true,
					width: 100,
					render: (value: any) => {
						return (
							<Paragraph
								ellipsis={{
									expandable: true,
									rows: 3,
									symbol: (
										<span style={{ color: "#9254de" }}>plus</span>
									),
								}}
							>
								{value}
							</Paragraph>
						);
					},
				};
			});
		if (labels && labels.indexOf("Sector") !== -1) {
			cols.push({
				title: "Méta secteur",
				dataIndex: ["attributes", "middle"],
				render: renderMetaSector,
				sorter: true,
				width: 100,
			});
			cols.push({
				title: "Occurrences",
				width: 150,
				align: "center",
				dataIndex: ["attributes", "_sort_count"],
				render: (_: Map<any>, node: Node) => {
					const { _from, _to } = node.attributes;
					return <div>{(_from?.length ?? 0) + (_to?.length ?? 0)}</div>;
				},
				sorter: true,
			});
		}
		if (labels && labels.indexOf("Sponsor") !== -1) {
			cols.push({
				title: "Secteur d'activité",
				dataIndex: ["attributes"],
				render: renderSponsor,
				sorter: false,
			});
		}
		cols.push({
			title: "Actions",
			dataIndex: ["attributes", "uid"],
			render: renderEdit,
			fixed: "left",
			width: 100,
		});
	}
	// #endregion

	// #region tableGen
	const pagination: TablePaginationConfig = {
		pageSize: fetched.size ?? 20,
		current: fetched.page ?? 1,
		total: fetched.count,
		showTotal: function (total: number, range: [number, number]): React.ReactNode {
			return total > (fetched?.size ?? 20)
				? `${range[0]}-${range[1]} de ${total} éléments`
				: `Total: ${total} éléments`;
		},
		position: ["topRight", "bottomRight"],
		onChange: fetchOnChange,
		onShowSizeChange: (_, size) => changeSize(size, labels),
	};

	let table: JSX.Element;
	if (fetched.values) {
		const searched: JSX.Element[] = Object.keys(fetched.src).reduce((acc, cur) => {
			const values = fetched.src[cur];
			const updater = updateSrcKV(cur);
			return values.length > 0
				? [
						...acc,
						...values.map((v, i) => {
							function onClose() {
								const newValues = [...values];
								newValues.splice(i);
								updater(newValues);
							}
							return (
								// eslint-disable-next-line react/jsx-no-bind
								<Tag onClick={onClose} onClose={onClose} closable>
									{defaultMasks[cur] ?? cur}:{" "}
									{typeof v === "string"
										? v.length > 20
											? `${v.substring(0, 20)}...`
											: v
										: v}
								</Tag>
							);
						}),
				  ]
				: acc;
		}, [] as JSX.Element[]);
		const {
			src: { _all },
		} = fetched;
		const setAll = updateSrcKV("_all");
		const canMerge = selectedNodes.length > 1;
		table = (
			<Table
				title={() => (
					<>
						<Row
							style={{ marginLeft: 16, marginRight: 16 }}
							justify="space-between"
							align="middle"
						>
							<Space align="center">
								<Title level={3} style={{ marginTop: 10 }}>
									{labelTitle}
								</Title>
								<Button type="link" onClick={reIndexCurried}>
									<SyncOutlined />
								</Button>
								{canMerge
									? `${selectedNodes.length} lignes sélectionnées`
									: ""}
								<UniqueSearchBar all={_all ?? []} setAll={setAll} />
							</Space>
						</Row>
						{searched.length > 0 ? <Row>{searched}</Row> : null}
					</>
				)}
				dataSource={noded}
				scroll={{
					x: 300,
					scrollToFirstRowOnChange: true,
				}}
				sticky
				bordered
				size="small"
				onChange={onTableChange}
				columns={cols}
				pagination={pagination}
				expandable={
					labels.includes("IDX_BOAMP")
						? {}
						: {
								expandedRowRender: renderExpandedRow,
						  }
				}
				rowClassName={(_rec, index) =>
					edited.indexOf(index) !== -1 ? "table-row-edit" : ""
				}
			/>
		);
		if (loading) {
			table = <Spin>{table}</Spin>;
		}
	} else {
		table = <Spin size="large">Chargement en cours...</Spin>;
	}
	// #endregion
	return table;
}

export default connector(NodeListEdit);
